/**
 * 원문 표시 창 — 연동 키 발급·웹훅 서명 키 새로 생성 공용.
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — 「키 발급 흐름」 2·3, D-3, AC-22
 *
 * 원문은 이 창에서 한 번만 보인다. 배경 클릭·ESC로 닫히면 원문을 잃으므로 [확인했습니다]로만 닫는다.
 * 공용 ConfirmPopup은 ESC를 처리하지 않고, 배경 클릭은 `backdropClose:false`면 무시한다.
 * 그래서 `showSecretReveal`이 이 옵션을 고정해서 연다(호출부가 옵션을 고를 수 없게).
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import { ConfirmManager, ToastManager } from "components/organisms/CommonUI";
import type { ProvisionResult } from "api/partnerAdminService";
import { ModalBody, ModalTitle, Notice, RawBox, ButtonRow } from "./style";

export const ONE_TIME_NOTICE = "이 키는 지금 한 번만 보입니다. 창을 닫으면 다시 볼 수 없습니다.";
/** 연동 키 발급 — 원문이 보관됐을 때. 이제는 [원문 복사]로 다시 꺼낼 수 있다. */
export const ISSUE_RAW_STORED_NOTICE = "나중에 [원문 복사]로 다시 복사할 수 있습니다";

export const provisionResultText = (provision: ProvisionResult): string =>
  provision.ok ? "상대 서버에 적용했습니다" : `상대 서버에 넣지 못했습니다: ${provision.message}`;

export interface SecretRevealModalProps {
  title: string;
  raw: string;
  provision: ProvisionResult | null;
  /** 창 상단 안내 — 호출부가 넘긴다(발급·대체 경로마다 다름). 기본은 「한 번만 보임」 */
  notice?: string;
  onConfirm: () => void;
}

const SecretRevealModal: React.FC<SecretRevealModalProps> = ({ title, raw, provision, notice, onConfirm }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // 클립보드가 막힌 환경 — 원문은 선택 가능한 상자에 그대로 있어 직접 복사할 수 있다.
      ToastManager.show({ content: "복사하지 못했습니다. 원문을 직접 선택해 복사하세요.", duration: 3000 });
    }
  };

  return (
    <ModalBody data-testid="secret-reveal">
      <ModalTitle>{title}</ModalTitle>
      <Notice $tone="warn">{notice ?? ONE_TIME_NOTICE}</Notice>
      {/* amp-mask: Amplitude 세션 녹화에서 원문을 가린다(D2 완화책) */}
      <RawBox className="amp-mask" data-testid="secret-reveal-raw" aria-label="원문">
        {raw}
      </RawBox>
      <ButtonRow>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Primary"
          onClick={handleCopy}
          data-testid="secret-reveal-copy"
        >
          {copied ? "복사했습니다" : "복사"}
        </Button>
      </ButtonRow>
      {provision && (
        <Notice $tone={provision.ok ? "info" : "warn"} data-testid="secret-reveal-provision">
          {provisionResultText(provision)}
        </Notice>
      )}
      <Button
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        onClick={onConfirm}
        data-testid="secret-reveal-confirm"
      >
        확인했습니다
      </Button>
    </ModalBody>
  );
};

/** 원문 표시 창을 연다 — 배경 클릭·닫기 버튼 없음 고정(AC-22). */
export const showSecretReveal = (params: Omit<SecretRevealModalProps, "onConfirm">) => {
  ConfirmManager.show({
    body: <SecretRevealModal {...params} onConfirm={() => ConfirmManager.hide()} />,
    showBackdrop: true,
    backdropClose: false,
    showCloseButton: false,
  });
};

export default SecretRevealModal;
